/**
 * TreatX Provider Onboarding Context — Phase 2C
 * ===============================================
 * REMOVED: sessionStorage for providers and pendingApplications
 * REMOVED: mock providers and mock pending applications
 * 
 * Backend APIs used:
 *   GET  /api/providers                   — approved providers
 *   PUT  /api/providers/:id/suspend       — suspend provider
 *   PUT  /api/providers/:id/reinstate     — reinstate provider
 *   GET  /api/admin/applications          — pending/all applications
 *   POST /api/provider/applications       — submit new application
 *
 * currentOnboarding (multi-step form wizard) remains in React state — UI-only.
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuthGate } from '../hooks/useAuthGate';

// TREATX-BATCH-BOOT-01 — role predicate: provider onboarding + admin
// applications are TreatX-admin-scope only. Do NOT fetch anonymously
// or for other roles.
const _PO_ROLES = ['admin', 'treatx_admin', 'super_admin', 'platform_admin'];
import axios from 'axios';

// Phase B RCA-5 (2026-05-20): security helper. The TreatX-side admin endpoints
// (/api/admin/applications and /api/providers/{id}/suspend|reinstate) MUST be
// called with a Bearer token. Previously this context used raw axios without
// the auth interceptor, which is why the backend admin_router had been left
// anonymous-readable as a compatibility shim — leaking 52 applications + PII
// to anonymous callers. We now always attach the canonical
// `treatx_unified_token` so the backend guard can hard-enforce
// `entity.view` permission.
const _authHeaders = () => {
  const token = (typeof localStorage !== 'undefined')
    ? null
    : null;
  return {};
};

const ProviderOnboardingContext = createContext();
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useProviderOnboarding = () => {
  const context = useContext(ProviderOnboardingContext);
  if (!context) {
    throw new Error('useProviderOnboarding must be used within ProviderOnboardingProvider');
  }
  return context;
};

// Provider Types — PRD1.9: aligned with locked architecture
export const PROVIDER_TYPES = {
  hospital: { id: 'hospital', name: 'Hospital', description: 'Multi-department, multi-doctor facility', legalFields: ['entityType', 'legalName', 'tradeRegistryNumber', 'taxOffice', 'taxNumber', 'registeredAddress'] },
  clinic: { id: 'clinic', name: 'Clinic', description: 'Focused services, limited scope', legalFields: ['entityType', 'legalName', 'tradeRegistryNumber', 'taxOffice', 'taxNumber', 'registeredAddress'] },
  doctor_practice: { id: 'doctor_practice', name: 'Doctor Practice', description: 'A legal entity owned by an individual doctor. Create this option if you operate your own private practice and need to receive bookings, contracts and payments through your business.', legalFields: ['fullName', 'medicalTitle', 'nationalId', 'practiceAddress'] },
  agency: { id: 'agency', name: 'Agency', description: 'Medical tourism agency', legalFields: ['entityType', 'legalName', 'tradeRegistryNumber', 'taxOffice', 'taxNumber', 'registeredAddress'] },
};

export const SUBSCRIPTION_PACKAGES = {
  flash: { id: 'flash', name: 'Flash', monthlyPrice: 5000, currency: 'TRY', marketplaceAccess: false, maxUsers: 3, crmAccess: true, treatxLeads: false, leadsPerMonth: 0, escrow: false, color: 'gray', description: 'CRM-only entry package', features: ['Up to 3 users', 'Clinic-owned patient CRM', 'Basic reporting', 'Email support'] },
  flash_plus: { id: 'flash_plus', name: 'Flash+', monthlyPrice: 10000, currency: 'TRY', marketplaceAccess: true, maxUsers: 5, crmAccess: true, treatxLeads: true, leadsPerMonth: 20, escrow: true, color: 'blue', recommended: true, description: 'Entry to marketplace', features: ['Up to 5 users', 'TreatX Marketplace listing', '20 leads/month', 'Full CRM access', 'Escrow payment support'] },
  gold: { id: 'gold', name: 'Gold', monthlyPrice: 20000, currency: 'TRY', marketplaceAccess: true, maxUsers: 30, crmAccess: true, treatxLeads: true, leadsPerMonth: 200, escrow: true, color: 'amber', description: 'Growth clinics', features: ['Up to 30 users', 'Premium placement', '200 leads/month', 'Advanced analytics'] },
  platinum: { id: 'platinum', name: 'Platinum', monthlyPrice: 30000, currency: 'TRY', marketplaceAccess: true, maxUsers: -1, crmAccess: true, treatxLeads: true, leadsPerMonth: -1, escrow: true, color: 'purple', description: 'Enterprise', features: ['Unlimited users', 'Top placement', 'Unlimited leads', 'White-label options', 'API access'] },
};

export const ONBOARDING_STATUS = {
  DRAFT: 'draft', PROVIDER_TYPE_SELECTED: 'provider_type_selected', LEGAL_INFO_SUBMITTED: 'legal_info_submitted',
  GEOLOCATION_SET: 'geolocation_set', PACKAGE_SELECTED: 'package_selected', PAYMENT_PENDING: 'payment_pending',
  PAID_PENDING_VALIDATION: 'paid_pending_validation', DOCUMENTS_UPLOADED: 'documents_uploaded',
  PENDING_ADMIN_REVIEW: 'pending_admin_review', APPROVED: 'approved', REJECTED: 'rejected', SUSPENDED: 'suspended',
};

export const VALIDITY_PERIODS = [
  { value: 1, label: '1 month' }, { value: 3, label: '3 months' }, { value: 6, label: '6 months' },
  { value: 12, label: '12 months' }, { value: 24, label: '24 months' }, { value: 'custom', label: 'Custom date range' },
];

export const PACKAGE_STATUS = { NOT_SELECTED: 'not_selected', SELECTED: 'selected', ACTIVE: 'active', EXPIRED: 'expired', PAYMENT_PENDING: 'payment_pending' };

export const REQUIRED_DOCUMENTS = {
  treatx_documents: [
    { id: 'kvkk_gdpr', name: 'KVKK / GDPR Consent', description: 'Data protection agreement', required: true, hasTemplate: true, cmsKey: 'provider_kvkk_gdpr_doc' },
    { id: 'service_agreement', name: 'TreatX Service Agreement', description: 'Platform terms and conditions', required: true, hasTemplate: true, cmsKey: 'provider_service_agreement_doc' },
    { id: 'confidentiality', name: 'Confidentiality Agreement', description: 'Non-disclosure agreement', required: true, hasTemplate: true, cmsKey: 'provider_confidentiality_doc' },
  ],
  provider_documents: [
    { id: 'trade_registry', name: 'Trade Registry Gazette', description: 'Official registration document', required: true, hasTemplate: false },
    { id: 'signature_circular', name: 'Signature Circular', description: 'Authorized signatures document', required: true, hasTemplate: false },
    { id: 'medical_licenses', name: 'Medical Licenses', description: 'Professional certifications', required: true, hasTemplate: false },
    { id: 'additional', name: 'Additional Documents', description: 'Any other supporting documents', required: false, hasTemplate: false },
  ],
};

const normalizeProvider = (p) => ({
  id: p.provider_id || p.id,
  provider_id: p.provider_id || p.clinic_id || p.id,
  provider_type: p.provider_type || 'clinic',
  clinic_id: p.clinic_id || p.provider_id || p.id,  // backward compat
  status: p.status || 'approved',
  name: p.brand_name || p.legal_entity_name || p.name || p.legal_name || p.trade_name || '',
  legalInfo: {
    legalName: p.legal_entity_name || p.name || p.legal_name || '',
    tradeName: p.brand_name || p.name || p.trade_name || '',
    email: p.email || '',
    phone: p.phone || '',
  },
  geolocation: { city: p.city || '', country: p.country || '', district: p.district || '' },
  package: p.package,
  approvedAt: p.approved_at || p.created_at,
  validUntil: p.valid_until,
  linkedClinicId: p.linked_clinic_id,
  suspendedAt: p.suspended_at,
  suspensionReason: p.suspension_reason,
  activeDoctorAffiliations: p.active_doctor_affiliations || 0,
  pendingDoctorAffiliations: p.pending_doctor_affiliations || 0,
  verifiedDoctorCount: p.verified_doctor_count || 0,
  verificationStatus: p.verification_status || 'unverified',
});

const normalizeApplication = (a) => ({
  id: a.application_id,
  referenceNumber: a.application_id,
  providerType: a.provider_type || 'clinic',
  status: a.status === 'pending' ? ONBOARDING_STATUS.PENDING_ADMIN_REVIEW : a.status,
  submittedAt: a.submitted_at,
  createdAt: a.submitted_at || a.created_at,
  legalInfo: {
    legalName: a.legal_entity_name || a.clinic_name || '',
    tradeName: a.brand_name || a.clinic_name || '',
    email: a.email,
    phone: a.phone,
  },
  geolocation: { city: a.city, country: a.country },
  package: null,
  documents: {},
  certifications: [],
  applicationId: a.application_id,
});

const generateReferenceNumber = () => {
  const prefix = 'TRX';
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}-${timestamp}-${random}`;
};

export const ProviderOnboardingProvider = ({ children }) => {
  const [providers, setProviders] = useState([]);
  const [currentOnboarding, setCurrentOnboarding] = useState(null);
  const [pendingApplications, setPendingApplications] = useState([]);

  // Fetch from backend
  const fetchProviders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/providers`, { headers: _authHeaders() });
      setProviders((data.providers || []).map(normalizeProvider));
    } catch (err) { console.error('[ProviderOnboarding] fetchProviders error:', err); }
  }, []);

  const fetchApplications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/applications`, { headers: _authHeaders() });
      setPendingApplications((data.applications || []).map(normalizeApplication));
    } catch (err) { console.error('[ProviderOnboarding] fetchApplications error:', err); }
  }, []);

  // TREATX-BATCH-BOOT-01 — canonical auth-ready + role gate.
  const { authorized: _po_authorized } = useAuthGate({ roles: _PO_ROLES });
  useEffect(() => {
    if (!_po_authorized) return;
    fetchProviders();
    fetchApplications();
  }, [_po_authorized, fetchProviders, fetchApplications]);

  // PROVIDER-ONBOARDING-CLEANUP-01 (2026-02): the previous resolveGeolocation
  // was a 5-entry keyword mock that silently defaulted every non-matched
  // address to Istanbul, TR — stamping fake coordinates onto every clinic
  // outside 5 hardcoded cities. Removed. Country/city are now collected as
  // typed; lat/lng are never invented. setGeolocationData below persists
  // address+country+city without inventing coordinates.

  const startOnboarding = useCallback(() => {
    const o = { id: `onboarding-${Date.now()}`, step: 1, status: ONBOARDING_STATUS.DRAFT, createdAt: new Date().toISOString(), providerType: null, legalInfo: {}, geolocation: null, package: null, payment: null, documents: {}, referenceNumber: null };
    setCurrentOnboarding(o);
    return o;
  }, []);

  const updateOnboarding = useCallback((updates) => { setCurrentOnboarding(prev => ({ ...prev, ...updates })); }, []);
  const setProviderType = useCallback((type) => { setCurrentOnboarding(prev => ({ ...prev, providerType: type, status: ONBOARDING_STATUS.PROVIDER_TYPE_SELECTED, step: 2 })); }, []);
  const setLegalInfo = useCallback((legalInfo) => { setCurrentOnboarding(prev => ({ ...prev, legalInfo, status: ONBOARDING_STATUS.LEGAL_INFO_SUBMITTED, step: 3 })); }, []);

  const setGeolocationData = useCallback(({ address, country, city }) => {
    const geoData = { address: address || '', country: country || '', city: city || '' };
    setCurrentOnboarding(prev => ({ ...prev, geolocation: geoData, status: ONBOARDING_STATUS.GEOLOCATION_SET, step: 4 }));
    return geoData;
  }, []);

  const setPackageSelection = useCallback((packageId) => { setCurrentOnboarding(prev => ({ ...prev, package: packageId, status: ONBOARDING_STATUS.PACKAGE_SELECTED, step: 5 })); }, []);

  const processPayment = useCallback((paymentMethod, paymentData) => {
    const referenceNumber = generateReferenceNumber();
    setCurrentOnboarding(prev => ({ ...prev, payment: { method: paymentMethod, status: paymentMethod === 'online' ? 'completed' : 'pending_verification', referenceNumber, ...paymentData, processedAt: new Date().toISOString() }, referenceNumber, status: ONBOARDING_STATUS.PAID_PENDING_VALIDATION, step: 6 }));
    return referenceNumber;
  }, []);

  const confirmReferenceNumber = useCallback((inputRef) => {
    if (currentOnboarding?.referenceNumber === inputRef) {
      setCurrentOnboarding(prev => ({ ...prev, referenceConfirmed: true, step: 7 }));
      return true;
    }
    return false;
  }, [currentOnboarding]);

  const uploadDocument = useCallback((documentId, fileData) => {
    setCurrentOnboarding(prev => ({ ...prev, documents: { ...prev.documents, [documentId]: { ...fileData, uploadedAt: new Date().toISOString(), status: 'uploaded' } } }));
  }, []);

  // Submit for review — persists to backend
  const submitForReview = useCallback(async () => {
    if (!currentOnboarding) return null;
    try {
      const { data } = await axios.post(`${API_URL}/api/provider/applications`, {
        email: currentOnboarding.legalInfo?.email || currentOnboarding.contactEmail || '',
        legalName: currentOnboarding.legalInfo?.legalName || '',
        fullName: currentOnboarding.legalInfo?.contactName || '',
        phone: currentOnboarding.legalInfo?.phone || '',
        providerType: currentOnboarding.providerType || 'clinic',
        country: currentOnboarding.geolocation?.country || '',
        city: currentOnboarding.geolocation?.city || '',
        address: currentOnboarding.geolocation?.address || '',
        source: 'provider_onboarding',
      });
      setCurrentOnboarding(null);
      await fetchApplications();
      return data.application;
    } catch (err) {
      console.error('[ProviderOnboarding] submitForReview error:', err);
      return null;
    }
  }, [currentOnboarding, fetchApplications]);

  const getPendingApplications = useCallback(() => pendingApplications.filter(a => 
    a.status === ONBOARDING_STATUS.PENDING_ADMIN_REVIEW ||
    a.status === 'pending' ||
    a.status === 'submitted' ||
    a.status === 'under_review' ||
    a.status === 'info_requested' ||
    a.status === 'resubmitted'
  ), [pendingApplications]);
  const getAllApplications = useCallback(() => pendingApplications, [pendingApplications]);

  // Phase B RCA-2 (2026-05-20): backend APP_TRANSITIONS requires
  // submitted → under_review → approved|rejected. The admin UI used to call
  // /approve directly which 400'd ("Cannot transition from 'submitted' to
  // 'approved'"). Drive the canonical transition here so the admin only sees
  // a single Approve/Reject action.
  const _ensureUnderReview = async (applicationId) => {
    const app = pendingApplications.find(a => a.id === applicationId || a.applicationId === applicationId);
    const status = app?.status;
    if (status === 'submitted' || status === 'resubmitted') {
      try {
        await axios.put(`${API_URL}/api/admin/applications/${applicationId}/review`, null, { headers: _authHeaders() });
      } catch (err) {
        // 400 here means another admin already advanced state — safe to ignore.
        console.warn('[ProviderOnboarding] _ensureUnderReview transition note:', err?.response?.data || err);
      }
    }
  };

  const approveApplication = useCallback(async (applicationId) => {
    try {
      await _ensureUnderReview(applicationId);
      await axios.put(`${API_URL}/api/admin/applications/${applicationId}/approve`, null, { headers: _authHeaders() });
      await fetchApplications();
      await fetchProviders();
    } catch (err) { console.error('[ProviderOnboarding] approve error:', err); }
  }, [fetchApplications, fetchProviders, pendingApplications]);

  const rejectApplication = useCallback(async (applicationId, reason) => {
    try {
      await _ensureUnderReview(applicationId);
      await axios.put(`${API_URL}/api/admin/applications/${applicationId}/reject`, { reason }, { headers: _authHeaders() });
      await fetchApplications();
    } catch (err) { console.error('[ProviderOnboarding] reject error:', err); }
  }, [fetchApplications, pendingApplications]);

  const suspendProvider = useCallback(async (providerId, reason) => {
    try {
      await axios.put(`${API_URL}/api/providers/${providerId}/suspend`, { reason }, { headers: _authHeaders() });
      await fetchProviders();
    } catch (err) { console.error('[ProviderOnboarding] suspend error:', err); }
  }, [fetchProviders]);

  const reinstateProvider = useCallback(async (providerId, newValidUntil) => {
    try {
      await axios.put(`${API_URL}/api/providers/${providerId}/reinstate`, { valid_until: newValidUntil }, { headers: _authHeaders() });
      await fetchProviders();
    } catch (err) { console.error('[ProviderOnboarding] reinstate error:', err); }
  }, [fetchProviders]);

  const getProviderById = useCallback((providerId) => providers.find(p => p.id === providerId), [providers]);
  // Bounded Rebuild: new provider lifecycle is pending_activation → active → suspended.
  // Treat any of {approved, pending_activation, active} as "approved and listed".
  const getApprovedProviders = useCallback(() => providers.filter(p =>
    p.status === 'approved' || p.status === ONBOARDING_STATUS.APPROVED ||
    p.status === 'pending_activation' || p.status === 'active'
  ), [providers]);
  const isProviderApproved = useCallback((providerId) => providers.find(p => p.id === providerId)?.status === ONBOARDING_STATUS.APPROVED, [providers]);
  const isProviderSuspended = useCallback((providerId) => providers.find(p => p.id === providerId)?.status === ONBOARDING_STATUS.SUSPENDED, [providers]);
  const getSuspendedProviders = useCallback(() => providers.filter(p => p.status === ONBOARDING_STATUS.SUSPENDED), [providers]);

  const getProviderLimits = useCallback((providerId) => {
    const provider = providers.find(p => p.id === providerId);
    return provider?.package ? SUBSCRIPTION_PACKAGES[provider.package] : null;
  }, [providers]);

  const getProviderPackageStatus = useCallback((providerId) => {
    const provider = providers.find(p => p.id === providerId);
    if (!provider) return PACKAGE_STATUS.NOT_SELECTED;
    if (!provider.package) return PACKAGE_STATUS.NOT_SELECTED;
    if (provider.validUntil && new Date(provider.validUntil) < new Date()) return PACKAGE_STATUS.EXPIRED;
    return PACKAGE_STATUS.ACTIVE;
  }, [providers]);

  const value = {
    providers, currentOnboarding, pendingApplications,
    PROVIDER_TYPES, SUBSCRIPTION_PACKAGES, ONBOARDING_STATUS, REQUIRED_DOCUMENTS, VALIDITY_PERIODS, PACKAGE_STATUS,
    startOnboarding, updateOnboarding, setProviderType, setLegalInfo, setGeolocationData, setPackageSelection,
    processPayment, confirmReferenceNumber, uploadDocument, submitForReview,
    getPendingApplications, getAllApplications, approveApplication, rejectApplication, suspendProvider, reinstateProvider,
    getProviderById, getApprovedProviders, isProviderApproved, isProviderSuspended, getSuspendedProviders, getProviderLimits, getProviderPackageStatus,
    refreshApplications: fetchApplications, refreshProviders: fetchProviders,
  };

  return (
    <ProviderOnboardingContext.Provider value={value}>
      {children}
    </ProviderOnboardingContext.Provider>
  );
};

export default ProviderOnboardingContext;
